import { memo } from 'react';
import { Box, Container, Header, Link, SpaceBetween, Table } from '@amzn/awsui-components-core';
import {
  AttachmentReadonlyRow,
  useAttachmentsReadonlyTabViewModel,
} from './AttachmentsReadonlyTabViewModel';

/** The "Attachments" tab body on the read-only view page: a download-only list of attached files. */
export const AttachmentsReadonlyTabView = memo(function AttachmentsReadonlyTabView() {
  const vm = useAttachmentsReadonlyTabViewModel();

  if (!vm) return null;

  return (
    <Container
      header={
        <Header
          variant="h3"
          counter={`(${vm.rows.length})`}
          description="Supplemental files attached to this content."
        >
          Attachments
        </Header>
      }
    >
      <SpaceBetween size="s">
        <Table<AttachmentReadonlyRow>
          columnDefinitions={[
            {
              id: 'name',
              header: 'Name',
              cell: (row) =>
                row.downloadPath ? (
                  <Link href={row.downloadPath} external target="_blank">
                    {row.filename}
                  </Link>
                ) : (
                  <Box>{row.filename}</Box>
                ),
            },
          ]}
          items={vm.rows as AttachmentReadonlyRow[]}
          trackBy="attachmentId"
          variant="embedded"
          loading={vm.isLoadingAttachments}
          loadingText="Loading attachments..."
          empty={
            vm.isSomeAttachmentLoadFailed ? (
              <Box color="text-status-warning">
                Attachments could not be loaded. Try again shortly.
              </Box>
            ) : (
              <Box color="inherit">No attachments.</Box>
            )
          }
        />
        {vm.isSomeAttachmentLoadFailed && vm.rows.length > 0 && (
          <Box color="text-status-warning" fontSize="body-s">
            Some attachments could not be loaded. The list may be incomplete — try again shortly.
          </Box>
        )}
        {(() => {
          const count = vm.rows.filter((row) => row.downloadPath === undefined).length;
          if (count === 0) return null;
          return (
            <Box color="text-body-secondary" fontSize="body-s">
              {`${count} attachment${count === 1 ? '' : 's'} cannot be downloaded because ${count === 1 ? 'it is' : 'they are'} being scanned or did not pass the scan`}
            </Box>
          );
        })()}
      </SpaceBetween>
    </Container>
  );
});
